/**
 * Annotation layer — the drawing / tracing engine.
 *
 * Interaction contract, matching MedDream / OHIF / RadiAnt:
 *
 *   Length / Rect / Ellipse  — click-hold-drag-release.
 *   Angle   (3 points)       — click → click → click, rubber-band from
 *                              previous point to cursor between clicks.
 *   Cobb    (4 points, 2 lines) — drag line 1, then drag line 2.
 *   Freehand                 — click-drag-release, polygonised on up.
 *   None (select mode)       — hover highlights, click selects,
 *                              Delete/Backspace removes selected.
 *
 * Every visible element previews *live* — the user always sees the
 * shape growing as they move. Below a 3-pixel drag distance the down
 * + up is treated as a single "click", so the tool can distinguish
 * drag vs click intent for tools that accept both.
 *
 * Coordinates are always stored in IMAGE-pixel space. The parent
 * `viewport` store provides screen→image / image→screen transforms
 * so the SVG visually co-registers with the CanvasGL renderer at any
 * zoom / pan / flip.
 */
import { createSignal, createMemo, For, Show, Switch, Match, onMount, onCleanup } from "solid-js";
import {
  type Measurement, type Point2D, pxDist, angleDeg, cobbDeg,
  ellipseStats, nextPinLabel,
} from "../measure/types";
import type { ViewportState } from "./store";
import { classes, activeClass, classById } from "../labeling/classes";

export type Tool =
  | "none" | "pan" | "zoom" | "window"
  | "length" | "angle" | "cobb" | "ellipse" | "rect" | "freehand"
  | "text" | "arrow" | "pin"
  | "bbox" | "polygon" | "keypoint" | "mask";

export interface AnnotationLayerProps {
  viewport:      ViewportState;
  measurements:  Measurement[];
  onAdd:         (m: Measurement) => void;
  onDelete?:     (id: string) => void;
  instanceUid:   string;
  activeTool:    Tool;
  stored?:       Uint16Array | Int16Array | Uint8Array;
  rescaleSlope?: number;
  rescaleIntercept?: number;
  pixelSpacingMm?: [number, number];
  onPointerMove?: (imgX: number, imgY: number) => void;
}

// ---------- Drawing state machine ----------
type DrawState =
  | { kind: "idle" }
  // 2-point tools — user is dragging from startImg; liveImg updates on move.
  | { kind: "drag2";  startImg: Point2D; liveImg: Point2D; tool: "length" | "rect" | "ellipse" | "arrow" | "bbox" }
  // Polygon — committed vertices so far, liveImg is the cursor for the
  // rubber-band to the next click. Closes on click near the first
  // vertex, on Enter, or on Escape (cancel).
  | { kind: "polygon"; points: Point2D[]; liveImg: Point2D }
  // Angle — 1, 2 or 3 committed points; liveImg is the cursor.
  | { kind: "angle1"; p1: Point2D; liveImg: Point2D }
  | { kind: "angle2"; p1: Point2D; p2: Point2D; liveImg: Point2D }
  // Cobb — 2 or 3 committed points; uses same vertex sequence as angle but
  // finalises on the 4th click (two distinct lines p1-p2, p3-p4).
  | { kind: "cobb1"; p1: Point2D; liveImg: Point2D }
  | { kind: "cobb2"; p1: Point2D; p2: Point2D; liveImg: Point2D }
  | { kind: "cobb3"; p1: Point2D; p2: Point2D; p3: Point2D; liveImg: Point2D }
  // Freehand
  | { kind: "freehand"; points: Point2D[] };

const DRAG_EPS_PX = 3;  // screen-px threshold that separates click vs drag

export function AnnotationLayer(p: AnnotationLayerProps) {
  const v = p.viewport;

  const [draw, setDraw]     = createSignal<DrawState>({ kind: "idle" });
  const [hoverId, setHover] = createSignal<string | null>(null);
  const [selId, setSel]     = createSignal<string | null>(null);
  // Text-entry overlay — when set, renders an <input> at the given
  // image point. Committed on Enter / blur; cancelled on Escape.
  const [pendingText, setPendingText] = createSignal<{ imgPt: Point2D } | null>(null);
  // Debug HUD counters — lets us see from the UI whether pointerdown /
  // pointermove / pointerup are actually firing on the host div.
  const [dbgDown, setDbgDown] = createSignal(0);
  const [dbgMove, setDbgMove] = createSignal(0);
  const [dbgUp,   setDbgUp]   = createSignal(0);
  let downAt: { sx: number; sy: number } | null = null;

  // All pointer handling now lives on the host <div> below.
  // (Legacy SVG-based handlers removed.)

  // Tool change / Escape / Enter — cancel or commit in-progress state
  onMount(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "Escape") setDraw({ kind: "idle" });
      if (e.key === "Enter") {
        const d = draw();
        if (d.kind === "polygon" && d.points.length >= 3) {
          finalizePolygon([...d.points]);
          setDraw({ kind: "idle" });
        }
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selId()) {
        p.onDelete?.(selId()!);
        setSel(null);
      }
    };
    window.addEventListener("keydown", kd);
    onCleanup(() => window.removeEventListener("keydown", kd));
  });

  // ---------- Finalize helpers ----------
  function baseRec(points: Point2D[]) {
    return {
      id: crypto.randomUUID(),
      instanceUid: p.instanceUid,
      frameNumber: 1,
      points,
      pixelSpacingMm: p.pixelSpacingMm,
      createdAtUnix: Math.floor(Date.now() / 1000),
    };
  }
  function finalize2(tool: "length" | "rect" | "ellipse" | "arrow" | "bbox", a: Point2D, b: Point2D) {
    if (tool === "length") {
      p.onAdd({ ...baseRec([a, b]), kind: "length",
                derivedMm: toMm(pxDist(a, b), p.pixelSpacingMm) });
    } else if (tool === "rect") {
      p.onAdd({ ...baseRec([a, b]), kind: "rect",
                derivedMm: rectAreaMm2(a, b, p.pixelSpacingMm) });
    } else if (tool === "arrow") {
      p.onAdd({ ...baseRec([a, b]), kind: "arrow" });
    } else if (tool === "bbox") {
      p.onAdd({ ...baseRec([a, b]), kind: "bbox", classId: activeClass().id });
    } else {
      const stats = p.stored && p.pixelSpacingMm
        ? ellipseStats(a, b, p.stored as any, v.imageW(), v.imageH(),
                       p.rescaleSlope ?? 1, p.rescaleIntercept ?? 0,
                       p.pixelSpacingMm[0], p.pixelSpacingMm[1])
        : undefined;
      p.onAdd({ ...baseRec([a, b]), kind: "ellipse", stats,
                derivedMm: stats?.areaMm2 });
    }
  }
  function finalizeText(at: Point2D, text: string) {
    if (!text.trim()) return;
    p.onAdd({ ...baseRec([at]), kind: "text", text: text.trim() });
  }
  function finalizePin(at: Point2D) {
    const label = nextPinLabel(p.measurements, p.instanceUid);
    p.onAdd({ ...baseRec([at]), kind: "pin", pinLabel: label });
  }
  function finalizePolygon(points: Point2D[]) {
    if (points.length < 3) return;
    p.onAdd({ ...baseRec(points), kind: "polygon", classId: activeClass().id });
  }
  function finalizeKeypoint(at: Point2D) {
    p.onAdd({ ...baseRec([at]), kind: "keypoint", classId: activeClass().id });
  }
  function finalizeAngle(a: Point2D, b: Point2D, c: Point2D) {
    p.onAdd({ ...baseRec([a, b, c]), kind: "angle", derivedMm: angleDeg(a, b, c) });
  }
  function finalizeCobb(a: Point2D, b: Point2D, c: Point2D, d: Point2D) {
    p.onAdd({ ...baseRec([a, b, c, d]), kind: "cobb", derivedMm: cobbDeg(a, b, c, d) });
  }
  function finalizeFreehand(pts: Point2D[]) {
    let px = 0;
    for (let i = 1; i < pts.length; i++) px += pxDist(pts[i - 1], pts[i]);
    px += pxDist(pts[pts.length - 1], pts[0]);
    p.onAdd({ ...baseRec(pts), kind: "freehand",
              derivedMm: toMm(px, p.pixelSpacingMm) });
  }

  // ---------- Rendering ----------
  const mine = () => p.measurements.filter((m) => m.instanceUid === p.instanceUid);

  // CSS transform to visually co-register the SVG's image-space
  // <g> with the renderer's zoom/pan/flip.
  const gTransform = () => {
    const s = v.scale();
    const o = v.imageOrigin();
    // We render to image-space coordinates; flips are handled in the
    // screenToImage math (so the hit-testing and the visual rendering
    // stay consistent).
    return `translate(${o.x}px, ${o.y}px) scale(${s})`;
  };

  const strokePx = () => 1.5 / Math.max(v.scale(), 0.01);
  const dotPx = () => 3.5 / Math.max(v.scale(), 0.01);

  // When the mask brush is active, let MaskCanvas below own the pointer.
  // Any view tool (none/pan/zoom/window) also bypasses us and goes to
  // the DICOM canvas below for its gesture.
  const eventsEnabled = () => !(p.activeTool === "none" || p.activeTool === "pan"
                           || p.activeTool === "zoom" || p.activeTool === "window"
                           || p.activeTool === "mask");

  let host: HTMLDivElement | undefined;

  // Convert a page-space event into image-space using the host div,
  // not the <svg>. The div is a plain HTML element, so its bounding
  // rect is reliable across browsers regardless of any SVG quirks
  // around pointer-events / hit-testing on empty space.
  function getImgFromDiv(e: PointerEvent): Point2D {
    if (!host) return { x: 0, y: 0 };
    const rect = host.getBoundingClientRect();
    return v.screenToImage(e.clientX - rect.left, e.clientY - rect.top);
  }

  function onPointerDownDiv(e: PointerEvent) {
    setDbgDown((n) => n + 1);
    if (e.button !== 0) return;
    const t = p.activeTool;
    if (t === "none" || t === "pan" || t === "zoom" || t === "window") return;

    try { host?.setPointerCapture(e.pointerId); } catch { /* fine */ }
    const img = getImgFromDiv(e);
    downAt = { sx: e.clientX, sy: e.clientY };

    if (t === "length" || t === "rect" || t === "ellipse" || t === "arrow" || t === "bbox") {
      setDraw({ kind: "drag2", startImg: img, liveImg: img, tool: t });
      return;
    }
    if (t === "freehand") {
      setDraw({ kind: "freehand", points: [img] });
      return;
    }
    // angle / cobb / text / pin / polygon / keypoint / mask:
    //   state advances on pointerUp (or via brush-drag for mask)
  }

  function onPointerMoveDiv(e: PointerEvent) {
    setDbgMove((n) => n + 1);
    const img = getImgFromDiv(e);
    p.onPointerMove?.(img.x, img.y);
    const d = draw();
    switch (d.kind) {
      case "drag2":  setDraw({ ...d, liveImg: img }); break;
      case "angle1": case "angle2":
      case "cobb1":  case "cobb2":  case "cobb3":
        setDraw({ ...d, liveImg: img } as DrawState); break;
      case "polygon": setDraw({ ...d, liveImg: img }); break;
      case "freehand": {
        const last = d.points[d.points.length - 1];
        if (!last || pxDist(last, img) > 1 / Math.max(v.scale(), 0.1)) {
          setDraw({ kind: "freehand", points: [...d.points, img] });
        }
        break;
      }
      case "idle": break;
    }
  }

  function onPointerUpDiv(e: PointerEvent) {
    setDbgUp((n) => n + 1);
    const t = p.activeTool;
    const img = getImgFromDiv(e);
    const cameFromDrag = !!downAt && (
      Math.hypot(e.clientX - downAt.sx, e.clientY - downAt.sy) > DRAG_EPS_PX
    );
    downAt = null;

    if (t === "none") { setSel(null); return; }
    if (t === "pan" || t === "zoom" || t === "window") return;

    const d = draw();

    if (t === "length" || t === "rect" || t === "ellipse" || t === "arrow" || t === "bbox") {
      if (d.kind !== "drag2") return;
      if (pxDist(d.startImg, d.liveImg) < 1) { setDraw({ kind: "idle" }); return; }
      finalize2(d.tool, d.startImg, d.liveImg);
      setDraw({ kind: "idle" });
      return;
    }

    // ---- Label tools (click to place) ----
    if (t === "pin") {
      finalizePin(img);
      return;
    }
    if (t === "keypoint") {
      finalizeKeypoint(img);
      return;
    }
    if (t === "text") {
      setPendingText({ imgPt: img });
      return;
    }

    // ---- Polygon (multi-click; close on click near first vertex) ----
    if (t === "polygon") {
      if (d.kind === "idle") {
        setDraw({ kind: "polygon", points: [img], liveImg: img });
        return;
      }
      if (d.kind === "polygon") {
        const first = d.points[0];
        // Close if clicked near the first vertex (and we have ≥3 vertices)
        // OR if the user double-clicks (distance 0 from the previous click).
        const closeDistImg = 8 / Math.max(v.scale(), 0.1);
        if (d.points.length >= 3 && pxDist(first, img) < closeDistImg) {
          finalizePolygon([...d.points]);
          setDraw({ kind: "idle" });
          return;
        }
        setDraw({ kind: "polygon", points: [...d.points, img], liveImg: img });
        return;
      }
    }
    if (t === "freehand") {
      if (d.kind !== "freehand") return;
      if (d.points.length >= 3) finalizeFreehand(d.points);
      setDraw({ kind: "idle" });
      return;
    }
    if (t === "angle") {
      if (d.kind === "idle")
        setDraw({ kind: "angle1", p1: img, liveImg: img });
      else if (d.kind === "angle1")
        setDraw({ kind: "angle2", p1: d.p1, p2: img, liveImg: img });
      else if (d.kind === "angle2") {
        finalizeAngle(d.p1, d.p2, img);
        setDraw({ kind: "idle" });
      }
      return;
    }
    if (t === "cobb") {
      if (d.kind === "idle")
        setDraw({ kind: "cobb1", p1: img, liveImg: img });
      else if (d.kind === "cobb1")
        setDraw({ kind: "cobb2", p1: d.p1, p2: img, liveImg: img });
      else if (d.kind === "cobb2")
        setDraw({ kind: "cobb3", p1: d.p1, p2: d.p2, p3: img, liveImg: img });
      else if (d.kind === "cobb3") {
        finalizeCobb(d.p1, d.p2, d.p3, img);
        setDraw({ kind: "idle" });
      }
      return;
    }
  }

  return (
    <div
      ref={host}
      class="absolute inset-0 h-full w-full"
      style={{
        // HTML divs have rock-solid pointer-event semantics; `auto`
        // on a div means "catch every click in this box" — none of
        // SVG's visiblePainted/fill subtleties apply.  When the
        // active tool is a view tool (pan/zoom/window/none) the
        // canvas below owns the gesture.
        "pointer-events": eventsEnabled() ? "auto" : "none",
        cursor: cursorFor(p.activeTool),
        "touch-action": "none",
        "user-select": "none",
      }}
      onPointerDown={onPointerDownDiv}
      onPointerMove={onPointerMoveDiv}
      onPointerUp={onPointerUpDiv}
      onPointerCancel={() => { setDraw({ kind: "idle" }); downAt = null; }}
      onContextMenu={(e) => e.preventDefault()}
    >
    {/* Debug HUD — shows live counts so we can tell whether
        pointerdown / move / up are actually firing. Remove once the
        pointer pipeline is confirmed working. */}
    <div class="pointer-events-none absolute bottom-14 left-2 z-20 rounded bg-black/70 px-2 py-1 font-mono text-[10px] text-white/80">
      tool=<span class="text-brand">{p.activeTool}</span>
      {" "}events d:<span class="text-brand">{dbgDown()}</span>
      {" "}m:<span class="text-brand">{dbgMove()}</span>
      {" "}u:<span class="text-brand">{dbgUp()}</span>
      {" "}state=<span class="text-brand">{draw().kind}</span>
    </div>

    <svg
      class="absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
      style={{ "pointer-events": "none" }}
    >
      <g style={{ transform: gTransform() }}>
        {/* Existing measurements ------------------------------------- */}
        <For each={mine()}>
          {(m) => (
            <Anno m={m}
                  strokePx={strokePx()}
                  dotPx={dotPx()}
                  hovered={hoverId() === m.id}
                  selected={selId() === m.id}
                  onHover={(h) => setHover(h ? m.id : null)}
                  onSelect={() => setSel(m.id)} />
          )}
        </For>

        <DrawPreview state={draw} strokePx={strokePx} dotPx={dotPx}
                     pixelSpacingMm={p.pixelSpacingMm} />
      </g>
    </svg>

    {/* Text input overlay — sits above the SVG so the user can type
        into a real HTML <input>. Position is computed from the
        viewport store so the input follows zoom/pan. */}
    <Show when={pendingText()}>
      {(p2) => {
        const screen = () => v.imageToScreen(p2().imgPt.x, p2().imgPt.y);
        return (
          <input
            autofocus
            type="text"
            placeholder="Label text… (Enter to save, Esc to cancel)"
            class="absolute z-20 w-48 rounded border border-brand bg-black/80 px-2 py-1 font-mono text-[12px] text-white outline-none ring-2 ring-brand focus:ring-brand"
            style={{
              left: `${screen().x}px`,
              top:  `${screen().y - 12}px`,
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                finalizeText(p2().imgPt, (e.currentTarget as HTMLInputElement).value);
                setPendingText(null);
              } else if (e.key === "Escape") {
                setPendingText(null);
              }
            }}
            onBlur={(e) => {
              finalizeText(p2().imgPt, (e.currentTarget as HTMLInputElement).value);
              setPendingText(null);
            }}
          />
        );
      }}
    </Show>

    </div>
  );
}

function cursorFor(t: Tool): string {
  switch (t) {
    case "none":   return "default";
    case "pan":    return "grab";
    case "zoom":   return "zoom-in";
    case "window": return "ns-resize";
    default:       return "crosshair";
  }
}

// ---------------------------------------------------------------------
//  Preview component. Each sub-kind is rendered by a dedicated
//  <Show when={memo()}>{(s) => ...}</Show> — the memo returns a typed
//  accessor that is *reactive*, so every pointerMove that updates
//  `draw()` causes the preview JSX to re-evaluate without a full tree
//  rebuild.  NEVER do `const s = props.state()` at the top of a
//  component — that snapshots the value and breaks reactivity.
// ---------------------------------------------------------------------
const STROKE = "#27A567";
const SOFT   = "#94A3B8";
const HI     = "#F8FAFC";

function DrawPreview(props: {
  state:    () => DrawState;
  strokePx: () => number;
  dotPx:    () => number;
  pixelSpacingMm?: [number, number];
}) {
  const sp = () => props.pixelSpacingMm;
  // Length in the user-visible unit: mm when pixel spacing is known
  // (diagnostic radiology almost always ships it), px otherwise with
  // a "px" suffix so the reader never mistakes the number for mm.
  const fmtLen = (a: Point2D, b: Point2D) => {
    const px = pxDist(a, b);
    const s = sp();
    if (!s) return `${px.toFixed(0)} px`;
    const mm = px * ((s[0] + s[1]) / 2);
    return mm >= 10 ? `${mm.toFixed(1)} mm` : `${mm.toFixed(2)} mm`;
  };
  const fmtArea = (wpx: number, hpx: number) => {
    const s = sp();
    if (!s) return `${wpx.toFixed(0)} × ${hpx.toFixed(0)} px`;
    const wmm = wpx * s[0];
    const hmm = hpx * s[1];
    return `${wmm.toFixed(1)} × ${hmm.toFixed(1)} mm²`;
  };
  // Per-kind typed memos — each one is null unless `state.kind` matches,
  // so the Show for that kind only activates then. Crucially, the
  // memo itself is a signal — when props.state() changes, the memo
  // re-runs, yielding a fresh snapshot that the JSX below can read.
  type D2 = Extract<DrawState, { kind: "drag2" }>;
  type A1 = Extract<DrawState, { kind: "angle1" }>;
  type A2 = Extract<DrawState, { kind: "angle2" }>;
  type C1 = Extract<DrawState, { kind: "cobb1" }>;
  type C2 = Extract<DrawState, { kind: "cobb2" }>;
  type C3 = Extract<DrawState, { kind: "cobb3" }>;
  type FH = Extract<DrawState, { kind: "freehand" }>;

  const drag2 = createMemo<D2 | null>(() => {
    const s = props.state(); return s.kind === "drag2" ? s : null;
  });
  const angle1 = createMemo<A1 | null>(() => {
    const s = props.state(); return s.kind === "angle1" ? s : null;
  });
  const angle2 = createMemo<A2 | null>(() => {
    const s = props.state(); return s.kind === "angle2" ? s : null;
  });
  const cobb1 = createMemo<C1 | null>(() => {
    const s = props.state(); return s.kind === "cobb1" ? s : null;
  });
  const cobb2 = createMemo<C2 | null>(() => {
    const s = props.state(); return s.kind === "cobb2" ? s : null;
  });
  const cobb3 = createMemo<C3 | null>(() => {
    const s = props.state(); return s.kind === "cobb3" ? s : null;
  });
  const free = createMemo<FH | null>(() => {
    const s = props.state(); return s.kind === "freehand" && s.points.length > 1 ? s : null;
  });
  type PG = Extract<DrawState, { kind: "polygon" }>;
  const polygon = createMemo<PG | null>(() => {
    const s = props.state(); return s.kind === "polygon" ? s : null;
  });

  const styled = () => ({
    fill: "none" as const, stroke: STROKE,
    "stroke-width": props.strokePx(),
    "stroke-dasharray": `${3 * props.strokePx()} ${2 * props.strokePx()}`,
    "stroke-linecap":  "round" as const,
    "stroke-linejoin": "round" as const,
  });

  return (
    <>
      {/* drag2 — branches by tool */}
      <Show when={drag2()}>{(s) => (
        <Switch>
          <Match when={s().tool === "length"}>
            <g {...styled()}>
              <line x1={s().startImg.x} y1={s().startImg.y}
                    x2={s().liveImg.x}  y2={s().liveImg.y} />
              <circle cx={s().startImg.x} cy={s().startImg.y}
                      r={props.dotPx() / 1.2} fill={STROKE} stroke="none" />
              <circle cx={s().liveImg.x}  cy={s().liveImg.y}
                      r={props.dotPx() / 1.2} fill={STROKE} stroke="none" />
              <Label x={(s().startImg.x + s().liveImg.x) / 2}
                     y={(s().startImg.y + s().liveImg.y) / 2}
                     t={fmtLen(s().startImg, s().liveImg)}
                     scale={props.strokePx()} soft />
            </g>
          </Match>
          <Match when={s().tool === "rect"}>
            <g {...styled()}>
              <rect
                x={Math.min(s().startImg.x, s().liveImg.x)}
                y={Math.min(s().startImg.y, s().liveImg.y)}
                width={Math.abs(s().liveImg.x - s().startImg.x)}
                height={Math.abs(s().liveImg.y - s().startImg.y)} />
              <Label x={Math.max(s().startImg.x, s().liveImg.x)}
                     y={Math.min(s().startImg.y, s().liveImg.y)}
                     t={fmtArea(Math.abs(s().liveImg.x - s().startImg.x), Math.abs(s().liveImg.y - s().startImg.y))}
                     scale={props.strokePx()} soft />
            </g>
          </Match>
          <Match when={s().tool === "ellipse"}>
            <g {...styled()}>
              <ellipse
                cx={(s().startImg.x + s().liveImg.x) / 2}
                cy={(s().startImg.y + s().liveImg.y) / 2}
                rx={Math.abs(s().liveImg.x - s().startImg.x) / 2}
                ry={Math.abs(s().liveImg.y - s().startImg.y) / 2} />
              <rect
                x={Math.min(s().startImg.x, s().liveImg.x)}
                y={Math.min(s().startImg.y, s().liveImg.y)}
                width={Math.abs(s().liveImg.x - s().startImg.x)}
                height={Math.abs(s().liveImg.y - s().startImg.y)}
                stroke={SOFT} stroke-width={props.strokePx() * 0.6} fill="none"
                stroke-dasharray={`${2 * props.strokePx()} ${3 * props.strokePx()}`} />
              <Label x={Math.max(s().startImg.x, s().liveImg.x)}
                     y={Math.min(s().startImg.y, s().liveImg.y)}
                     t={(() => {
                       const rxpx = Math.abs(s().liveImg.x - s().startImg.x) / 2;
                       const rypx = Math.abs(s().liveImg.y - s().startImg.y) / 2;
                       const sp0 = props.pixelSpacingMm;
                       if (!sp0) return `${(Math.PI * rxpx * rypx).toFixed(0)} px²`;
                       const a = Math.PI * (rxpx * sp0[0]) * (rypx * sp0[1]);
                       return `${a.toFixed(1)} mm²`;
                     })()}
                     scale={props.strokePx()} soft />
            </g>
          </Match>
          <Match when={s().tool === "arrow"}>
            <g {...styled()}>
              <ArrowShape
                tail={s().startImg} head={s().liveImg}
                strokePx={props.strokePx()}
                dashed />
            </g>
          </Match>
          <Match when={s().tool === "bbox"}>
            {(() => {
              const col = activeClass().color;
              return (
                <g fill={`${col}20`} stroke={col} stroke-width={props.strokePx()}
                   stroke-dasharray={`${3 * props.strokePx()} ${2 * props.strokePx()}`}>
                  <rect
                    x={Math.min(s().startImg.x, s().liveImg.x)}
                    y={Math.min(s().startImg.y, s().liveImg.y)}
                    width={Math.abs(s().liveImg.x - s().startImg.x)}
                    height={Math.abs(s().liveImg.y - s().startImg.y)} />
                </g>
              );
            })()}
          </Match>
        </Switch>
      )}</Show>

      {/* Polygon in progress — vertex dots + rubber band to cursor */}
      <Show when={polygon()}>{(s) => {
        const col = activeClass().color;
        return (
          <g fill="none" stroke={col} stroke-width={props.strokePx()}
             stroke-linecap="round" stroke-linejoin="round">
            <polyline points={s().points.map((p) => `${p.x},${p.y}`).join(" ")
                                + ` ${s().liveImg.x},${s().liveImg.y}`}
                      stroke-dasharray={`${3 * props.strokePx()} ${2 * props.strokePx()}`} />
            <For each={s().points}>{(pt, i) => (
              <circle cx={pt.x} cy={pt.y}
                      r={(i() === 0 ? 1.7 : 1.2) * props.dotPx()}
                      fill={i() === 0 ? col : HI} stroke={col}
                      stroke-width={props.strokePx() * 0.6} />
            )}</For>
          </g>
        );
      }}</Show>

      {/* angle — 1st point set, rubber band to cursor */}
      <Show when={angle1()}>{(s) => (
        <g {...styled()}>
          <line x1={s().p1.x} y1={s().p1.y} x2={s().liveImg.x} y2={s().liveImg.y} />
          <circle cx={s().p1.x} cy={s().p1.y} r={props.dotPx() / 1.2}
                  fill={STROKE} stroke="none" />
        </g>
      )}</Show>

      {/* angle — 2 points set, live degree at vertex */}
      <Show when={angle2()}>{(s) => (
        <g {...styled()}>
          <polyline points={`${s().p1.x},${s().p1.y} ${s().p2.x},${s().p2.y} ${s().liveImg.x},${s().liveImg.y}`} />
          <circle cx={s().p1.x} cy={s().p1.y} r={props.dotPx() / 1.2}
                  fill={STROKE} stroke="none" />
          <circle cx={s().p2.x} cy={s().p2.y} r={props.dotPx()}
                  fill={HI} stroke="none" />
          <Label x={s().p2.x} y={s().p2.y} dx={12} dy={-12}
                 t={`${angleDeg(s().p1, s().p2, s().liveImg).toFixed(1)}°`}
                 scale={props.strokePx()} />
        </g>
      )}</Show>

      {/* cobb — 1 point, rubber band */}
      <Show when={cobb1()}>{(s) => (
        <g {...styled()}>
          <line x1={s().p1.x} y1={s().p1.y} x2={s().liveImg.x} y2={s().liveImg.y} />
          <circle cx={s().p1.x} cy={s().p1.y} r={props.dotPx() / 1.2}
                  fill={STROKE} stroke="none" />
        </g>
      )}</Show>

      {/* cobb — 2 points, ghost cursor for next line start */}
      <Show when={cobb2()}>{(s) => (
        <g {...styled()}>
          <line x1={s().p1.x} y1={s().p1.y} x2={s().p2.x} y2={s().p2.y} />
          <circle cx={s().p1.x} cy={s().p1.y} r={props.dotPx() / 1.2}
                  fill={STROKE} stroke="none" />
          <circle cx={s().p2.x} cy={s().p2.y} r={props.dotPx() / 1.2}
                  fill={STROKE} stroke="none" />
          <circle cx={s().liveImg.x} cy={s().liveImg.y}
                  r={props.dotPx() / 1.5}
                  stroke={SOFT} fill="none" stroke-width={props.strokePx() * 0.8} />
        </g>
      )}</Show>

      {/* cobb — 3 points, rubber band line 2, live degrees */}
      <Show when={cobb3()}>{(s) => (
        <g {...styled()}>
          <line x1={s().p1.x} y1={s().p1.y} x2={s().p2.x} y2={s().p2.y} />
          <line x1={s().p3.x} y1={s().p3.y} x2={s().liveImg.x} y2={s().liveImg.y} />
          <circle cx={s().p1.x} cy={s().p1.y} r={props.dotPx() / 1.2} fill={STROKE} stroke="none" />
          <circle cx={s().p2.x} cy={s().p2.y} r={props.dotPx() / 1.2} fill={STROKE} stroke="none" />
          <circle cx={s().p3.x} cy={s().p3.y} r={props.dotPx() / 1.2} fill={STROKE} stroke="none" />
          <Label x={(s().p1.x + s().p2.x + s().p3.x + s().liveImg.x) / 4}
                 y={(s().p1.y + s().p2.y + s().p3.y + s().liveImg.y) / 4}
                 t={`Cobb ${cobbDeg(s().p1, s().p2, s().p3, s().liveImg).toFixed(1)}°`}
                 scale={props.strokePx()} />
        </g>
      )}</Show>

      {/* freehand */}
      <Show when={free()}>{(s) => (
        <g {...styled()}>
          <polyline points={s().points.map((p) => `${p.x},${p.y}`).join(" ")} />
        </g>
      )}</Show>
    </>
  );
}

// ---------------------------------------------------------------------
//  Committed annotations — static, hover-aware
// ---------------------------------------------------------------------
function Anno(p: {
  m: Measurement; strokePx: number; dotPx: number;
  hovered: boolean; selected: boolean;
  onHover: (b: boolean) => void;
  onSelect: () => void;
}) {
  const stroke = () =>
    p.selected ? "#FACC15" : p.hovered ? "#34D399" : "#27A567";
  const baseProps = () => ({
    "stroke-width": p.strokePx,
    stroke: stroke(),
    fill: "none" as const,
    "stroke-linecap": "round" as const,
    "stroke-linejoin": "round" as const,
    "pointer-events": "auto" as const,
    style: { cursor: "pointer" },
    onPointerEnter: () => p.onHover(true),
    onPointerLeave: () => p.onHover(false),
    onClick: (e: MouseEvent) => { e.stopPropagation(); p.onSelect(); },
  });
  const m = p.m;
  const text = renderText(m);

  if (m.kind === "length") {
    const [a, b] = m.points;
    return (
      <g {...baseProps()}>
        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
        <Dot p={a} r={p.dotPx / 1.2} />
        <Dot p={b} r={p.dotPx / 1.2} />
        <Label x={(a.x+b.x)/2} y={(a.y+b.y)/2} t={text} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "angle") {
    const [a, b, c] = m.points;
    return (
      <g {...baseProps()}>
        <polyline points={`${a.x},${a.y} ${b.x},${b.y} ${c.x},${c.y}`} />
        <Dot p={a} r={p.dotPx / 1.2} />
        <Dot p={b} r={p.dotPx}       fill="#F8FAFC" />
        <Dot p={c} r={p.dotPx / 1.2} />
        <Label x={b.x} y={b.y} dx={12} dy={-12} t={text} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "cobb") {
    const [p1, p2, p3, p4] = m.points;
    return (
      <g {...baseProps()}>
        <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} />
        <line x1={p3.x} y1={p3.y} x2={p4.x} y2={p4.y} />
        <For each={m.points}>{(pt) => <Dot p={pt} r={p.dotPx / 1.2} />}</For>
        <Label x={(p1.x+p2.x+p3.x+p4.x)/4}
               y={(p1.y+p2.y+p3.y+p4.y)/4}
               t={text} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "ellipse") {
    const [a, c] = m.points;
    const cx = (a.x + c.x) / 2, cy = (a.y + c.y) / 2;
    const rx = Math.abs(c.x - a.x) / 2, ry = Math.abs(c.y - a.y) / 2;
    return (
      <g {...baseProps()}>
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} />
        <Dot p={a} r={p.dotPx / 1.2} />
        <Dot p={c} r={p.dotPx / 1.2} />
        <Label x={cx + rx} y={cy - ry} dx={6} dy={6} t={text} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "rect") {
    const [a, c] = m.points;
    const x = Math.min(a.x, c.x), y = Math.min(a.y, c.y);
    const w = Math.abs(c.x - a.x), h = Math.abs(c.y - a.y);
    return (
      <g {...baseProps()}>
        <rect x={x} y={y} width={w} height={h} />
        <Dot p={a} r={p.dotPx / 1.2} />
        <Dot p={c} r={p.dotPx / 1.2} />
        <Label x={x + w} y={y} dx={6} dy={6} t={text} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "arrow") {
    const [a, b] = m.points;
    return (
      <g {...baseProps()}>
        <ArrowShape tail={a} head={b} strokePx={p.strokePx} headFill={stroke()} />
        <Show when={m.text}>
          <Label x={b.x} y={b.y} dx={10} dy={-10} t={m.text!} scale={p.strokePx} />
        </Show>
      </g>
    );
  }
  if (m.kind === "text") {
    const [at] = m.points;
    return (
      <g {...baseProps()}>
        <circle cx={at.x} cy={at.y} r={p.dotPx * 0.8}
                fill={stroke()} stroke="none" />
        <Label x={at.x} y={at.y} dx={10} dy={-6} t={m.text ?? ""} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "pin") {
    const [at] = m.points;
    // Scale the pin size relative to the current stroke width so it
    // stays ~28 CSS-px tall regardless of zoom.
    const R = p.strokePx * 7.5;
    return (
      <g {...baseProps()}>
        {/* Teardrop pin */}
        <path d={`M${at.x} ${at.y}
                 C${at.x - R*0.9} ${at.y - R*0.4}, ${at.x - R*0.9} ${at.y - R*1.2}, ${at.x} ${at.y - R*1.8}
                 C${at.x + R*0.9} ${at.y - R*1.2}, ${at.x + R*0.9} ${at.y - R*0.4}, ${at.x} ${at.y} Z`}
              fill={stroke()} stroke={stroke()} stroke-width={p.strokePx * 0.6} />
        {/* White inner disc for the letter */}
        <circle cx={at.x} cy={at.y - R * 0.95} r={R * 0.55}
                fill="#0F172A" stroke="none" />
        <text x={at.x} y={at.y - R * 0.95 + R * 0.25}
              text-anchor="middle"
              fill="#F8FAFC" stroke="none"
              font-size={R * 0.8} font-weight="700"
              font-family="JetBrains Mono,ui-monospace,monospace">
          {m.pinLabel ?? "?"}
        </text>
      </g>
    );
  }
  if (m.kind === "bbox") {
    const [a, c] = m.points;
    const cls = classById(m.classId);
    const col = cls?.color ?? "#EF4444";
    const x = Math.min(a.x, c.x), y = Math.min(a.y, c.y);
    const w = Math.abs(c.x - a.x), h = Math.abs(c.y - a.y);
    return (
      <g {...baseProps()} stroke={p.selected ? "#FACC15" : col}
         fill={`${col}25`} stroke-width={p.strokePx}>
        <rect x={x} y={y} width={w} height={h} />
        <Label x={x} y={y - 4} t={cls?.name ?? "?"} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "polygon") {
    const pts = m.points.map(pp => `${pp.x},${pp.y}`).join(" ");
    const cls = classById(m.classId);
    const col = cls?.color ?? "#EF4444";
    const centroid = (() => {
      let x = 0, y = 0;
      for (const pp of m.points) { x += pp.x; y += pp.y; }
      return { x: x / m.points.length, y: y / m.points.length };
    })();
    return (
      <g {...baseProps()} stroke={p.selected ? "#FACC15" : col}
         fill={`${col}30`} stroke-width={p.strokePx}>
        <polygon points={pts} />
        <Label x={centroid.x} y={centroid.y} t={cls?.name ?? "?"} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "keypoint") {
    const [at] = m.points;
    const cls = classById(m.classId);
    const col = cls?.color ?? "#EF4444";
    const r = p.dotPx * 1.3;
    return (
      <g {...baseProps()}>
        {/* Cross-hair marker */}
        <circle cx={at.x} cy={at.y} r={r}
                fill={`${col}30`} stroke={p.selected ? "#FACC15" : col}
                stroke-width={p.strokePx} />
        <circle cx={at.x} cy={at.y} r={p.strokePx * 0.9}
                fill={col} stroke="none" />
        <line x1={at.x - r * 1.5} y1={at.y} x2={at.x + r * 1.5} y2={at.y}
              stroke={p.selected ? "#FACC15" : col} stroke-width={p.strokePx * 0.5} />
        <line x1={at.x} y1={at.y - r * 1.5} x2={at.x} y2={at.y + r * 1.5}
              stroke={p.selected ? "#FACC15" : col} stroke-width={p.strokePx * 0.5} />
        <Label x={at.x} y={at.y} dx={r + 4} dy={-4}
               t={cls?.name ?? "?"} scale={p.strokePx} />
      </g>
    );
  }
  if (m.kind === "freehand") {
    const pts = m.points.map(pp => `${pp.x},${pp.y}`).join(" ");
    const last = m.points[m.points.length - 1] ?? { x: 0, y: 0 };
    return (
      <g {...baseProps()}>
        <polygon points={pts} />
        <Label x={last.x} y={last.y} dx={6} dy={-6} t={text} scale={p.strokePx} />
      </g>
    );
  }
  return <></>;
}

// ---------- Small helpers ----------
function Dot(p: { p: Point2D; r: number; fill?: string }) {
  return <circle cx={p.p.x} cy={p.p.y} r={p.r} fill={p.fill ?? "#27A567"} stroke="none" />;
}

/** Line from tail → head with a filled triangular arrowhead at head.
 *  Head size scales with stroke so it stays visible at any zoom.
 *
 *  NOTE: all geometry is wrapped in `createMemo` so that when `p.tail`
 *  or `p.head` update during a drag, the derived positions re-compute
 *  and SVG re-renders. Reading props into plain `const` at the top of
 *  the component body would snapshot the values at mount time and the
 *  drag preview would appear frozen.
 */
function ArrowShape(p: { tail: Point2D; head: Point2D; strokePx: number; dashed?: boolean; headFill?: string }) {
  const geo = createMemo(() => {
    const dx = p.head.x - p.tail.x;
    const dy = p.head.y - p.tail.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len, uy = dy / len;
    const px = -uy, py = ux;
    const headLen  = Math.max(p.strokePx * 5, 6);
    const headHalf = Math.max(p.strokePx * 2.5, 3);
    const baseX = p.head.x - ux * headLen;
    const baseY = p.head.y - uy * headLen;
    return {
      shaftEndX: p.head.x - ux * headLen * 0.6,
      shaftEndY: p.head.y - uy * headLen * 0.6,
      leftX:     baseX + px * headHalf,
      leftY:     baseY + py * headHalf,
      rightX:    baseX - px * headHalf,
      rightY:    baseY - py * headHalf,
    };
  });
  return (
    <>
      <line x1={p.tail.x} y1={p.tail.y}
            x2={geo().shaftEndX} y2={geo().shaftEndY}
            stroke-dasharray={p.dashed ? `${3 * p.strokePx} ${2 * p.strokePx}` : undefined} />
      <polygon
        points={`${p.head.x},${p.head.y} ${geo().leftX},${geo().leftY} ${geo().rightX},${geo().rightY}`}
        fill={p.headFill ?? "#27A567"} stroke="none" />
    </>
  );
}
function Label(p: { x: number; y: number; dx?: number; dy?: number; t: string; scale: number; soft?: boolean }) {
  // Counter-scale font size with viewport zoom so text stays at ~11
  // CSS-px regardless of zoom level.
  const fs = 11 * (p.scale / 1.5);
  const fill = p.soft ? "#CBD5E1" : "#F8FAFC";
  // Draw a dark outline halo so the label reads on both dark and
  // light backgrounds (radiography, inverted CT, MG). `paint-order`
  // renders stroke before fill so the halo stays behind the glyphs.
  return (
    <text x={p.x + (p.dx ?? 6)} y={p.y + (p.dy ?? -6)}
          fill={fill}
          stroke="#020617"
          stroke-width={Math.max(2.5 * p.scale, 2)}
          stroke-linejoin="round"
          paint-order="stroke fill"
          font-size={fs}
          font-weight="600"
          font-family="JetBrains Mono,ui-monospace,monospace">{p.t}</text>
  );
}
function toMm(px: number, sp?: [number, number]): number | undefined {
  if (!sp) return undefined;
  return px * ((sp[0] + sp[1]) / 2);
}
function rectAreaMm2(a: Point2D, c: Point2D, sp?: [number, number]): number | undefined {
  if (!sp) return undefined;
  return Math.abs(a.x - c.x) * Math.abs(a.y - c.y) * sp[0] * sp[1];
}
// Always return *something* readable. If pixel spacing is absent the
// measurement is shown in pixels rather than collapsing to a single
// em-dash.
function renderText(m: Measurement): string {
  const sp = m.pixelSpacingMm;
  switch (m.kind) {
    case "length": {
      if (m.derivedMm != null) return `${fmt(m.derivedMm)} mm`;
      const [a, b] = m.points;
      return `${pxDist(a, b).toFixed(0)} px`;
    }
    case "angle":
      return `${(m.derivedMm ?? 0).toFixed(1)}°`;
    case "cobb":
      return `Cobb ${(m.derivedMm ?? 0).toFixed(1)}°`;
    case "ellipse": {
      const [a, c] = m.points;
      const rxpx = Math.abs(c.x - a.x) / 2, rypx = Math.abs(c.y - a.y) / 2;
      if (m.stats) {
        return `${Math.round(m.stats.mean)} HU · σ${m.stats.stdDev.toFixed(0)} · ${m.stats.areaMm2.toFixed(1)} mm²`;
      }
      if (sp) {
        const areaMm2 = Math.PI * (rxpx * sp[0]) * (rypx * sp[1]);
        return `${areaMm2.toFixed(1)} mm²`;
      }
      return `${(Math.PI * rxpx * rypx).toFixed(0)} px²`;
    }
    case "rect": {
      const [a, c] = m.points;
      const wpx = Math.abs(a.x - c.x), hpx = Math.abs(a.y - c.y);
      if (sp) {
        const wmm = wpx * sp[0];
        const hmm = hpx * sp[1];
        return `${wmm.toFixed(1)} × ${hmm.toFixed(1)} mm (${(wmm * hmm).toFixed(1)} mm²)`;
      }
      return `${wpx.toFixed(0)} × ${hpx.toFixed(0)} px`;
    }
    case "freehand":
      if (m.derivedMm != null) return `${fmt(m.derivedMm)} mm`;
      {
        let lpx = 0;
        for (let i = 1; i < m.points.length; i++) lpx += pxDist(m.points[i-1], m.points[i]);
        if (sp) return `${(lpx * (sp[0] + sp[1]) / 2).toFixed(1)} mm`;
        return `${lpx.toFixed(0)} px`;
      }
    case "text":
      return m.text ?? "";
    case "arrow":
      return m.text ?? "";
    case "pin":
      return m.pinLabel ?? "";
    case "bbox":
    case "polygon":
    case "keypoint":
      return classById(m.classId)?.name ?? "?";
    default:
      return "";
  }
}
function fmt(n: number | undefined): string {
  return n == null ? "—" : (Math.abs(n) >= 10 ? n.toFixed(1) : n.toFixed(2));
}
